import { Singleton } from 'typescript-ioc/es5'
import aqe from '@pushly/aqe'
import { HttpMethod } from '@pushly/aqe/lib/enums'
import { IApiCallOptions, IApiResponse } from '@pushly/aqe/lib/interfaces'
import { AbstractRequestService } from '@pushly/aqe/lib/services/abstract-request-service'
import { AiGenerationDirective } from '@pushly/models/lib/structs/pushly-ai/ai-generation-directive'
import { CompiledDirective } from '@pushly/models/lib/structs/pushly-ai/compiled-directive'
import { CreateAiGenerationDirectiveRequest } from '@pushly/models/lib/structs/pushly-ai/create-ai-generation-directive-request'
import { UpdateAiGenerationDirectiveRequest } from '@pushly/models/lib/structs/pushly-ai/update-ai-generation-directive-request'
import { CompileAiGenerationDirectiveRequest } from '@pushly/models/lib/structs/pushly-ai/compile-ai-generation-directive-request'
import { CompileAndPersistAiGenerationDirectiveRequest } from '@pushly/models/lib/structs/pushly-ai/compile-and-persist-ai-generation-directive-request'

/**
 * Client for the sailfish AI generation directive CRUD (NS-2649):
 *   GET   /domains/:domainId/ai-generation-directives[?role=]
 *   POST  /domains/:domainId/ai-generation-directives
 *   PATCH /domains/:domainId/ai-generation-directives/:directiveId
 *
 * The sailfish controller runs with its payload case-converter disabled and
 * validates the snake-case `@pushly/models` structs, so this service uses those
 * structs directly with `skipCaseConversion` (no camelCase bridging).
 */
@Singleton
export class AiGenerationDirectiveService extends AbstractRequestService {
    public constructor() {
        super()
    }

    /** Resolve the domain's Brand Voice directive (the singleton `brand_voice`), or null. */
    public async fetchBrandVoice(
        domainId: number,
        options: IApiCallOptions = {},
    ): Promise<IApiResponse<AiGenerationDirective | null>> {
        const res = await this.request(HttpMethod.GET, `${this.getEndpoint(domainId)}?role=brand_voice`, {
            ...options,
            config: { skipCaseConversion: true },
            responseType: [AiGenerationDirective],
        })

        return { ...res, data: res.data?.[0] ?? null }
    }

    public async create(
        domainId: number,
        dto: CreateAiGenerationDirectiveRequest,
        options: IApiCallOptions = {},
    ): Promise<IApiResponse<AiGenerationDirective>> {
        return this.request(HttpMethod.POST, this.getEndpoint(domainId), {
            ...options,
            data: dto,
            config: { skipCaseConversion: true },
            responseType: AiGenerationDirective,
        })
    }

    public async update(
        domainId: number,
        directiveId: number,
        dto: UpdateAiGenerationDirectiveRequest,
        options: IApiCallOptions = {},
    ): Promise<IApiResponse<AiGenerationDirective>> {
        return this.request(HttpMethod.PATCH, `${this.getEndpoint(domainId)}/${directiveId}`, {
            ...options,
            data: dto,
            config: { skipCaseConversion: true },
            responseType: AiGenerationDirective,
        })
    }

    /**
     * Compile a free-text directive body into its decomposition (NS-2668) without
     * persisting. Returns the tiered {@link CompiledDirective}: the guaranteed `spec`
     * (a depth-1 PQuery document), judge-checked `assertions`, best-effort `style`,
     * and `unmapped` residue. Server-side compile via sealab, Redis-cached by body hash.
     */
    public async compilePreview(
        domainId: number,
        body: string,
        options: IApiCallOptions = {},
    ): Promise<IApiResponse<CompiledDirective>> {
        // return { data: sportsIllustratedRes, ok: true }
        return this.request(HttpMethod.POST, `${this.getEndpoint(domainId)}/compile-preview`, {
            ...options,
            data: CompileAiGenerationDirectiveRequest.from({ body }),
            config: { skipCaseConversion: true },
            responseType: CompiledDirective,
        })
    }

    /**
     * Compile the directive's current active version server-side and persist the result
     * onto that version's `compiled_rules` (NS-2668 confirm write-path) — reuses
     * `compilePreview`'s Redis pre-warm, so this is typically instant right after a save.
     *
     * `expectedCompiledRules` must be the {@link CompiledDirective} the caller already
     * displayed and had approved for this version's body (normally straight from a prior
     * `compilePreview` call). The server independently recompiles and only persists if its
     * own fresh result matches — this call rejects (409) rather than silently persisting a
     * decomposition that drifted from what was actually reviewed.
     */
    public async compileAndPersist(
        domainId: number,
        directiveId: number,
        expectedCompiledRules: CompiledDirective,
        options: IApiCallOptions = {},
    ): Promise<IApiResponse<AiGenerationDirective>> {
        return this.request(HttpMethod.POST, `${this.getEndpoint(domainId)}/${directiveId}/compile`, {
            ...options,
            data: CompileAndPersistAiGenerationDirectiveRequest.from({
                expected_compiled_rules: expectedCompiledRules,
            }),
            config: { skipCaseConversion: true },
            responseType: AiGenerationDirective,
        })
    }

    private getEndpoint(domainId: number): string {
        return `${aqe.defaults.publicApiDomain}/domains/${domainId}/ai-generation-directives`
    }
}

// const sportsIllustratedRes = CompiledDirective.from({
// 		"assertions": [
// 			"Never in all caps",
// 			"Attribute what isn't established fact",
// 			"Never speculate",
// 			"Stay non-partisan",
// 			"Avoid politically loaded framing",
// 			"Handle tragedy and violence with restraint — no graphic or sensational language",
// 			"Only use a 'Breaking' label for genuinely breaking developments"
// 		],
// 		"style": "Authoritative, neutral, and precise — the tone of record.",
// 		"unmapped": [],
// 		"spec": {
// 			"and": [
// 				{
// 					"title.charLength": {
// 						"lte": 60
// 					}
// 				},
// 				{
// 					"message.raw": {
// 						"not_contains": "shocking"
// 					}
// 				},
// 				{
// 					"message.raw": {
// 						"not_contains": "slams"
// 					}
// 				},
// 				{
// 					"message.raw": {
// 						"not_contains": "you won't believe"
// 					}
// 				},
// 				{
// 					"message.hasEmoji": {
// 						"eq": false
// 					}
// 				},
// 				{
// 					"message.hasExclamation": {
// 						"eq": false
// 					}
// 				}
// 			]
// 		}
// })
